import {useTranslation, Trans} from 'react-i18next'
import {Anchor, ArrowLeftIcon, Button} from '@translated/babele-react'
import {LegendImage} from './LegendImage'
import {En2DeImage} from './En2DeImage'
import {En2EsImage} from './En2EsImage'
import {En2FrImage} from './En2FrImage'
import {En2ItImage} from './En2ItImage'
import {En2JaImage} from './En2JaImage'
import {En2KoImage} from './En2KoImage'
import {En2PtImage} from './En2PtImage'
import {En2RuImage} from './En2RuImage'
import {En2ZhImage} from './En2ZhImage'
import {ActionFunctionArgs, MetaFunction, NavLink} from 'react-router'
import {SingularityImage} from '~/routes/about/SingularityImage'
import {TranslatedLogo} from '~/routes/about/TranslatedLogo'
import {StatisticsImage} from '~/routes/about/StatisticsImage'
import {enrichedMeta} from '~/utils/meta.server'
import {TWebsiteLink} from '~/features/TLink/TWebsiteLink'

export const handle = {
  i18n: 'lara-evaluation'
}

export const meta: MetaFunction<typeof loader> = ({data}) => {
  if (!data?.meta) return []

  return data.meta
}

export const loader = async ({request}: ActionFunctionArgs) => {
  return {
    meta: await enrichedMeta({
      with: ['og'],
      request,
      namespace: 'lara-evaluation'
    })
  }
}

export {ErrorBoundary} from '~/features/RootErrorBoundary'

export default function About() {
  const {t} = useTranslation('lara-evaluation')

  return (
    <main className="w-full text-neutral-1000" data-theme="translated-light">
      <section className="flex flex-col items-center gap-6 px-3 py-16 text-center text-neutral-25 lg:gap-10 lg:px-4 lg:py-[6rem]">
        <div>
          <h1 className="max-w-[65rem] text-[2rem] font-bold leading-none lg:text-[4rem]">
            {t('hero_section.title')}
          </h1>
        </div>
        <p className="max-w-[60rem] text-lg translated-light:text-light-violet-400">
          <Trans i18nKey="hero_section.description" t={t} components={{br: <br />}} />
        </p>
      </section>

      <section className="rounded-t-lg bg-neutral-25 px-4">
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-16 py-16 lg:gap-[6rem] lg:py-[6rem]">
          <h2 className="max-w-[60rem] text-center text-display-md font-bold lg:text-display-lg">
            {t('lara_accuracy_section.title')}
          </h2>
        </div>
        <div className="border-charts mx-auto flex max-w-7xl flex-col justify-center">
          <div dir="ltr">
            <StatisticsImage />
          </div>
          <p className="mx-auto max-w-[75%] py-[2em] text-center text-[2vw] leading-[1.2] lg:text-[1.1vw]">
            {t('statistics_image.disclaimer', {ns: 'about'})}
          </p>
        </div>
        <div className="mx-auto flex max-w-[60rem] flex-col gap-16 py-8 lg:gap-[6rem] lg:py-[6rem]">
          <p className="text-lg text-neutral-800">
            {t('lara_accuracy_section.subsection_1.paragraph_1')}
          </p>
          <div className="flex flex-col gap-2">
            <h2 className="text-display-sm font-medium lg:text-display-md">
              {t('lara_accuracy_section.subsection_2.title')}
            </h2>
            <p className="text-lg text-neutral-800">
              {t('lara_accuracy_section.subsection_2.paragraph_1')}
            </p>
            <ul className="list-disc pl-8 text-lg text-neutral-800">
              <li>{t('lara_accuracy_section.subsection_2.list.list_element_1')}</li>
              <li>{t('lara_accuracy_section.subsection_2.list.list_element_2')}</li>
              <li>{t('lara_accuracy_section.subsection_2.list.list_element_3')}</li>
              <li>{t('lara_accuracy_section.subsection_2.list.list_element_4')}</li>
            </ul>
          </div>
          <div className="flex flex-col gap-4">
            <h2 className="text-display-sm font-medium lg:text-display-md">
              {t('lara_accuracy_section.subsection_3.title')}
            </h2>
            <div className="flex flex-col gap-8">
              <div className="flex flex-col gap-2">
                <h3 className="text-lg font-medium leading-none">
                  {t('lara_accuracy_section.subsection_3.step_1.title')}
                </h3>
                <p className="text-lg text-neutral-800">
                  {t('lara_accuracy_section.subsection_3.step_1.description')}
                </p>
              </div>
              <div className="flex flex-col gap-2">
                <h3 className="text-lg font-medium leading-none">
                  {t('lara_accuracy_section.subsection_3.step_2.title')}
                </h3>
                <p className="text-lg text-neutral-800">
                  {t('lara_accuracy_section.subsection_3.step_2.description')}
                </p>
              </div>
              <div className="flex flex-col gap-2">
                <h3 className="text-lg font-medium leading-none">
                  {t('lara_accuracy_section.subsection_3.step_3.title')}
                </h3>
                <p className="text-lg text-neutral-800">
                  {t('lara_accuracy_section.subsection_3.step_3.description')}
                </p>
              </div>
              <div className="flex flex-col gap-2">
                <h3 className="text-lg font-medium leading-none">
                  {t('lara_accuracy_section.subsection_3.step_4.title')}
                </h3>
                <p className="text-lg text-neutral-800">
                  {t('lara_accuracy_section.subsection_3.step_4.description')}
                </p>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <h2 className="text-display-sm font-medium lg:text-display-md">
              {t('lara_accuracy_section.subsection_4.title')}
            </h2>
            <p className="text-lg text-neutral-800">
              {t('lara_accuracy_section.subsection_4.description')}
            </p>
          </div>
        </div>
      </section>
      <section className="bg-neutral-25 px-4">
        <div className="border-charts mx-auto max-w-7xl px-4 lg:px-0">
          <div className="relative grid grid-cols-1 gap-4 py-6 lg:mx-[6rem] lg:grid-cols-2">
            <div
              className="sticky top-0 col-span-1 flex justify-center bg-neutral-25/8 p-4 backdrop-blur-lg lg:col-span-2"
              dir="ltr"
            >
              <LegendImage />
            </div>

            <div className="flex justify-center">
              <En2DeImage />
            </div>
            <div className="flex justify-center">
              <En2EsImage />
            </div>
            <div className="flex justify-center">
              <En2FrImage />
            </div>
            <div className="flex justify-center">
              <En2ItImage />
            </div>
            <div className="flex justify-center">
              <En2JaImage />
            </div>
            <div className="flex justify-center">
              <En2KoImage />
            </div>
            <div className="flex justify-center">
              <En2PtImage />
            </div>
            <div className="flex justify-center">
              <En2RuImage />
            </div>
            <div className="flex justify-center">
              <En2ZhImage />
            </div>
            <div className="col-span-1 flex justify-center lg:col-span-2">
              <p className="max-w-[75%] pb-[1em] text-center text-[2vw] leading-[1.2] lg:text-[1.1vw]">
                {t('statistics_image.disclaimer', {ns: 'about'})}
              </p>
            </div>
          </div>
        </div>

        <div className="my-16 flex justify-center">
          <Button
            className="hidden lg:flex"
            variant={{mode: 'outline', size: 'lg'}}
            render={
              <TWebsiteLink to="/about-lara">{t('promotion_section.dive_deep_link')}</TWebsiteLink>
            }
          >
            <ArrowLeftIcon />
            {t('back_button')}
          </Button>
          <Button
            className="flex lg:hidden"
            variant={{mode: 'outline', size: 'md'}}
            render={
              <TWebsiteLink to="/about-lara">{t('promotion_section.dive_deep_link')}</TWebsiteLink>
            }
          >
            <ArrowLeftIcon />
            {t('back_button')}
          </Button>
        </div>

        <div className="my-6 w-full border-t-1 border-neutral-300"></div>

        <div className="mx-auto flex max-w-[60rem] flex-col gap-16 py-16 lg:gap-[6rem] lg:py-[6rem]">
          <h2 className="mx-auto max-w-[60rem] text-center text-display-md font-bold lg:text-display-lg">
            {t('comparison_section.title')}
          </h2>
        </div>
        <div className="border-charts mx-auto my-8 flex max-w-7xl flex-col justify-center">
          <div dir="ltr">
            <SingularityImage />
          </div>
          <p className="mx-auto max-w-[75%] py-[2em] text-center text-[2vw] leading-[1.2] lg:text-[1.1vw]">
            {t('statistics_image.disclaimer', {ns: 'about'})}
          </p>
        </div>
        <div className="mx-auto flex max-w-[60rem] flex-col gap-16 py-16 lg:gap-[6rem] lg:py-[6rem]">
          <p className="text-lg text-neutral-800">
            {t('comparison_section.subsection_1.paragraph_1')}
          </p>
          <div className="flex flex-col gap-2">
            <h2 className="text-display-sm font-medium lg:text-display-md">
              {t('comparison_section.subsection_2.title')}
            </h2>
            <p className="text-lg text-neutral-800">
              {t('comparison_section.subsection_2.description')}
            </p>
          </div>
          <div className="flex flex-col gap-4">
            <h2 className="text-display-sm font-medium lg:text-display-md">
              {t('comparison_section.subsection_3.title')}
            </h2>
            <div className="flex flex-col gap-8">
              <div className="flex flex-col gap-2">
                <h3 className="text-lg font-medium leading-none">
                  {t('comparison_section.subsection_3.step_1.title')}
                </h3>
                <p className="text-lg text-neutral-800">
                  {t('comparison_section.subsection_3.step_1.description')}
                </p>
              </div>
              <div className="flex flex-col gap-2">
                <h3 className="text-lg font-medium leading-none">
                  {t('comparison_section.subsection_3.step_2.title')}
                </h3>
                <p className="text-lg text-neutral-800">
                  {t('comparison_section.subsection_3.step_2.description')}
                </p>
              </div>
              <div className="flex flex-col gap-2">
                <h3 className="text-lg font-medium leading-none">
                  {t('comparison_section.subsection_3.step_3.title')}
                </h3>
                <p className="text-lg text-neutral-800">
                  {t('comparison_section.subsection_3.step_3.description')}
                </p>
              </div>
              <div className="flex flex-col gap-2">
                <h3 className="text-lg font-medium leading-none">
                  {t('comparison_section.subsection_3.step_4.title')}
                </h3>
                <p className="text-lg text-neutral-800">
                  {t('comparison_section.subsection_3.step_4.description')}
                </p>
              </div>
              <div className="flex flex-col gap-2">
                <h3 className="text-lg font-medium leading-none">
                  {t('comparison_section.subsection_3.step_5.title')}
                </h3>
                <p className="text-lg text-neutral-800">
                  {t('comparison_section.subsection_3.step_5.description')}
                </p>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <h2 className="text-display-sm font-medium lg:text-display-md">
              {t('comparison_section.subsection_4.title')}
            </h2>
            <p className="text-lg text-neutral-800">
              {t('comparison_section.subsection_4.description')}
            </p>
          </div>
          <div className="flex justify-center">
            <Button
              className="hidden lg:flex"
              variant={{mode: 'outline', size: 'lg'}}
              render={<NavLink to="/about-lara">{t('promotion_section.dive_deep_link')}</NavLink>}
            >
              <ArrowLeftIcon />
              {t('back_button')}
            </Button>
            <Button
              className="flex lg:hidden"
              variant={{mode: 'outline', size: 'md'}}
              render={<NavLink to="/about-lara">{t('promotion_section.dive_deep_link')}</NavLink>}
            >
              <ArrowLeftIcon />
              {t('back_button')}
            </Button>
          </div>
        </div>
      </section>

      <section className="bg-light-violet-100 px-4">
        <div className="mx-auto flex max-w-[60rem] flex-col items-center gap-16 py-16 text-center lg:gap-[6rem] lg:py-[6rem]">
          <p>{t('footer_section.language_importance')}</p>
          <p className="text-display-sm font-medium lg:text-display-md">
            {t('footer_section.understand_and_be_understood')}
          </p>
          <Anchor href="https://translated.com" target="_blank">
            <div className="scale-75 lg:scale-100">
              <TranslatedLogo />
            </div>
          </Anchor>
        </div>
      </section>
    </main>
  )
}
